// Shared screen props + public-formula helpers (mirrors what the harness bots
// may legally compute: PlayerView + content only, never hidden state).

import type { ContentPack, MarketDef, MarketId, PlayerView, TechTreeDef, ViewProject } from "../../engine/types.js";
import type { TickContext } from "../../engine/ctx.js";
import type { GameSession, SessionSnapshot } from "../session.js";
import { planProjectEffort } from "../../engine/formulas/effort.js";
import { pFirstSiliconWorks } from "../../engine/formulas/bugs.js";
import { interpolate } from "../../engine/calendar.js";

export interface ScreenProps {
  snap: SessionSnapshot;
  content: ContentPack;
  session: GameSession;
}

/** A read-only TickContext over the PLAYER VIEW, for calling the engine's pure
 *  formula reads (pitchScore, archFitFactor, frontierAttrs, effectiveEmCost…)
 *  from screens. Those functions touch only ctx.s's view-visible fields and
 *  ctx.content — never the RNG, ledger, or hidden state; anything that mutates
 *  or draws randomness would crash here, which is the point. */
export function viewCtx(view: PlayerView, content: ContentPack): TickContext {
  return { s: view, content, monthsPerTick: content.scenario.monthsPerTick } as unknown as TickContext;
}

/** The scenario's architecture tree, if it plays on one (P1 UI gate). */
export function activeTreeOf(content: ContentPack): TechTreeDef | null {
  if (!content.scenario.treeId) return null;
  return content.trees.find((t) => t.id === content.scenario.treeId) ?? null;
}

/** Public estimate of P(first silicon works) for a project (true λ carries hidden noise). */
export function estimatePWorks(project: ViewProject, content: ContentPack): number {
  const models = content.economy.models;
  const lambdaHat = planProjectEffort(Math.max(1, project.kGates), project.startedMonth, models, project.offPath).critLambda;
  const escapeMult = Math.pow(models.respinEscapeMult, project.respins);
  return pFirstSiliconWorks(lambdaHat, project.coverage, escapeMult);
}

/** Public estimate of P(layout kicks the chip back to design) if taped in at
 *  coverage `cov` (mirrors projects.ts finishLayout; true λ carries hidden noise). */
export function estimateRework(project: ViewProject, content: ContentPack, cov: number): number {
  const models = content.economy.models;
  const lambdaHat = planProjectEffort(Math.max(1, project.kGates), project.startedMonth, models, project.offPath).critLambda;
  const reworkLambda =
    models.rtlReworkCoeff * lambdaHat * (1 - cov) * Math.pow(models.rtlReworkEscapeMult, project.rtlReworks);
  return 1 - Math.exp(-reworkLambda);
}

/** Market complexity expectation at month m (demand-side Moore's law). */
export function expectedKGatesAt(market: MarketDef, m: number): number {
  return market.expectedKGatesBase * Math.pow(2, m / market.expectedKGatesDoublingMonths);
}

export function tamAt(market: MarketDef, m: number): number {
  return interpolate(market.tamUnitsPerMonth, m);
}

export function refAspAt(market: MarketDef, m: number): number {
  return interpolate(market.refAsp, m);
}

const MARKET_PALETTE = ["var(--market-1)", "var(--market-2)", "var(--market-3)", "var(--market-4)"];

/** Deterministic identity color for a market — always paired with the
 *  market's text label elsewhere on screen, never the sole identity
 *  channel (see theme.css's market palette comment). */
export function marketColor(id: MarketId): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return MARKET_PALETTE[Math.abs(h) % MARKET_PALETTE.length]!;
}
